import { useMutation, useQuery } from '@apollo/client/react';
import {
  Badge,
  Box,
  Button,
  DialogActionTrigger,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  HStack,
  Heading,
  Input,
  Table,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';

import { CenteredDialogContent } from '#components/ui/centered-dialog-content';
import { toaster } from '#components/ui/toaster';
import type { MyUserSecretsQuery } from '#graphql/generated';
import { CREATE_USER_SECRET } from '#graphql/mutations/create-user-secret';
import { DELETE_USER_SECRET } from '#graphql/mutations/delete-user-secret';
import { UPDATE_USER_SECRET } from '#graphql/mutations/update-user-secret';
import { MY_USER_SECRETS } from '#graphql/queries/my-user-secrets';
import { useAppConfig } from '#providers/app-config-provider';

type SecretRow = MyUserSecretsQuery['myUserSecrets'][number];

function UserSecretModal({
  open,
  onOpenChange,
  mode,
  apiHost,
  editing,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  apiHost: string;
  editing: SecretRow | null;
  onSaved: () => void;
}) {
  const [key, setKey] = useState('');
  const [description, setDescription] = useState('');
  const [plaintext, setPlaintext] = useState('');
  const [loadingPlaintext, setLoadingPlaintext] = useState(false);
  const [createSecret, { loading: creating }] = useMutation(CREATE_USER_SECRET);
  const [updateSecret, { loading: updating }] = useMutation(UPDATE_USER_SECRET);

  useEffect(() => {
    if (!open) return;

    if (mode === 'create') {
      setKey('');
      setDescription('');
      setPlaintext('');
      setLoadingPlaintext(false);
      return;
    }

    if (!editing) return;

    setKey(editing.key);
    setDescription(editing.description);
    setPlaintext('');
    setLoadingPlaintext(true);
    void (async () => {
      try {
        const res = await fetch(
          `${apiHost}/api/crypto/decode/user-secret/${encodeURIComponent(editing.id)}`,
          { credentials: 'include' },
        );
        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          const msg =
            typeof errBody.message === 'string'
              ? errBody.message
              : `HTTP ${res.status}`;
          throw new Error(msg);
        }

        const data = (await res.json()) as { plaintext?: string };
        if (typeof data.plaintext !== 'string') {
          throw new Error('Invalid response');
        }

        setPlaintext(data.plaintext);
      } catch (e) {
        toaster.create({
          title: 'Could not load secret value',
          description: e instanceof Error ? e.message : 'Request failed',
          type: 'error',
        });
        setPlaintext('');
      } finally {
        setLoadingPlaintext(false);
      }
    })();
  }, [open, mode, editing, apiHost]);

  const saving = creating || updating;

  const handleSave = useCallback(async () => {
    const trimmedKey = key.trim();
    if (!trimmedKey) {
      toaster.create({
        title: 'Key required',
        description: 'Enter a name for this secret.',
        type: 'warning',
      });
      return;
    }

    try {
      if (mode === 'create') {
        await createSecret({
          variables: {
            input: {
              key: trimmedKey,
              plaintextValue: plaintext,
              description: description.trim() || null,
            },
          },
        });
        toaster.create({
          title: 'Secret created',
          type: 'success',
          duration: 2500,
          closable: true,
        });
      } else if (editing) {
        await updateSecret({
          variables: {
            input: {
              id: editing.id,
              key: trimmedKey,
              plaintextValue: plaintext,
              description: description.trim() || null,
            },
          },
        });
        toaster.create({
          title: 'Secret updated',
          type: 'success',
          duration: 2500,
          closable: true,
        });
      }

      onSaved();
      onOpenChange(false);
    } catch (err: unknown) {
      const message =
        err &&
        typeof err === 'object' &&
        'message' in err &&
        typeof (err as { message: string }).message === 'string'
          ? (err as { message: string }).message
          : 'Save failed';
      toaster.create({
        title: 'Error',
        description: message,
        type: 'error',
      });
    }
  }, [
    key,
    description,
    plaintext,
    mode,
    editing,
    createSecret,
    updateSecret,
    onSaved,
    onOpenChange,
  ]);

  return (
    <DialogRoot
      open={open}
      onOpenChange={(e) => {
        if (!e.open) onOpenChange(false);
      }}
    >
      <CenteredDialogContent maxW="min(90vw, 32rem)">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Add secret' : 'Edit secret'}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack gap={3} align="stretch">
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1}>
                Key
              </Text>
              <Input
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="e.g. OPENAI_API_KEY"
                disabled={saving}
              />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1}>
                Description
              </Text>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional note"
                disabled={saving}
              />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1}>
                Value
              </Text>
              {mode === 'edit' && loadingPlaintext ? (
                <Text fontSize="sm" color="fg.muted">
                  Decrypting…
                </Text>
              ) : (
                <Textarea
                  value={plaintext}
                  onChange={(e) => setPlaintext(e.target.value)}
                  placeholder="Secret value"
                  rows={5}
                  fontFamily="mono"
                  fontSize="sm"
                  disabled={saving || (mode === 'edit' && loadingPlaintext)}
                />
              )}
            </Box>
          </VStack>
        </DialogBody>
        <DialogFooter>
          <HStack gap={2} flexWrap="wrap">
            <DialogActionTrigger asChild>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button
              colorPalette="blue"
              loading={saving}
              disabled={
                saving || (mode === 'edit' && loadingPlaintext) || !key.trim()
              }
              onClick={() => void handleSave()}
            >
              Save
            </Button>
          </HStack>
        </DialogFooter>
      </CenteredDialogContent>
    </DialogRoot>
  );
}

export function UserSecretsPage() {
  const { config } = useAppConfig();
  const { data, loading, error, refetch } = useQuery(MY_USER_SECRETS);
  const [deleteSecret] = useMutation(DELETE_USER_SECRET);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editing, setEditing] = useState<SecretRow | null>(null);

  const rows = data?.myUserSecrets ?? [];
  const apiHost = config.apiHost;

  const openCreate = () => {
    setModalMode('create');
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (row: SecretRow) => {
    setModalMode('edit');
    setEditing(row);
    setModalOpen(true);
  };

  const handleDelete = async (row: SecretRow) => {
    if (!window.confirm(`Delete secret "${row.key}"? This cannot be undone.`)) {
      return;
    }

    setDeletingId(row.id);
    try {
      const { data: delData } = await deleteSecret({
        variables: { id: row.id },
      });
      if (!delData?.deleteUserSecret) {
        toaster.create({
          title: 'Not found',
          description: 'Secret may have already been removed.',
          type: 'warning',
        });
        return;
      }

      toaster.create({
        title: 'Secret removed',
        type: 'success',
        duration: 2500,
        closable: true,
      });
      await refetch();
    } catch (err: unknown) {
      const message =
        err &&
        typeof err === 'object' &&
        'message' in err &&
        typeof (err as { message: string }).message === 'string'
          ? (err as { message: string }).message
          : 'Delete failed';
      toaster.create({
        title: 'Error',
        description: message,
        type: 'error',
      });
    } finally {
      setDeletingId(null);
    }
  };

  if (loading && !data) {
    return (
      <Box>
        <Heading size="lg" color="fg" mb={6}>
          User secrets
        </Heading>
        <Text>Loading…</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Heading size="lg" color="fg" mb={6}>
          User secrets
        </Heading>
        <Text color="red.500">Error: {error.message}</Text>
        <Button onClick={() => refetch()} mt={4}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <VStack gap={4} align="stretch">
        <Heading size="lg" color="fg">
          User secrets
        </Heading>
        <Text color="fg.muted" fontSize="sm">
          Store API keys and other sensitive values encrypted at rest. Values
          are decrypted only when you edit a secret in this app.
        </Text>
        <HStack gap={2} flexWrap="wrap">
          <Button size="sm" colorPalette="blue" onClick={openCreate}>
            Add secret
          </Button>
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            Refresh
          </Button>
        </HStack>
        {rows.length === 0 ? (
          <Text color="fg.muted">No secrets yet.</Text>
        ) : (
          <Box overflowX="auto">
            <Table.Root
              size="sm"
              variant="line"
              css={{
                '& thead tr, & tbody tr': {
                  backgroundColor: 'var(--input-base-background-color)',
                },
              }}
            >
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Key</Table.ColumnHeader>
                  <Table.ColumnHeader>Description</Table.ColumnHeader>
                  <Table.ColumnHeader>Storage</Table.ColumnHeader>
                  <Table.ColumnHeader>Updated</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="end"> </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {rows.map((row) => (
                  <Table.Row key={row.id}>
                    <Table.Cell fontWeight="medium">{row.key}</Table.Cell>
                    <Table.Cell>{row.description || '—'}</Table.Cell>
                    <Table.Cell>
                      <Badge size="sm" colorPalette="gray">
                        Encrypted
                      </Badge>
                    </Table.Cell>
                    <Table.Cell whiteSpace="nowrap">
                      {new Date(row.updatedAt).toLocaleString()}
                    </Table.Cell>
                    <Table.Cell textAlign="end">
                      <HStack gap={2} justify="flex-end" flexWrap="wrap">
                        <Button
                          size="xs"
                          variant="outline"
                          onClick={() => openEdit(row)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="xs"
                          variant="outline"
                          colorPalette="red"
                          loading={deletingId === row.id}
                          onClick={() => void handleDelete(row)}
                        >
                          Remove
                        </Button>
                      </HStack>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
      </VStack>
      <UserSecretModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        mode={modalMode}
        apiHost={apiHost}
        editing={editing}
        onSaved={() => void refetch()}
      />
    </Box>
  );
}
